import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Stack,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { CheckCheck, ChefHat, PackageCheck, Truck } from 'lucide-react';
import {
  useCompleteOrderMutation,
  useUpdateOrderStatusMutation,
} from '@/features/organizations/api/ordersApi';
import useSnackbar from '@/hooks/useSnackbar';
import { AnonymousUser, Order, OrderStatus, User } from '@/types';
import { addMinutes, format } from 'date-fns';
import DataCell from '@/components/DataCell';
import DataCard from './DataCard';
import {
  addCurrency,
  getPercentage,
  sumAmounts,
  multiplyByQuantity,
} from '@/utils/numbers';
import { useResponsive, useUserProfile } from '@/hooks';
import { nameWithFirstLastnameInitial } from '@/utils/names';
import useOrganizations from '@/features/organizations/useOrganizations';
import routes from '@/constants/routes';

const OrderDetailsTab = (order: Order) => {
  const [openSnackbar] = useSnackbar();
  const { currentOrganization } = useOrganizations();
  const { organizationId } = useParams();
  const { palette } = useTheme();
  const { getUserById } = useUserProfile();
  const { isMobile } = useResponsive();
  const [updateOrderStatus, isUpdatingStatusMutation] =
    useUpdateOrderStatusMutation();
  const [completeOrder] = useCompleteOrderMutation();
  const { navigate } = useNavigate();

  const handleClose = () => {
    navigate(
      routes.organizations.orders.main.replace(
        ':organizationId',
        organizationId ?? ''
      )
    );
  };

  const steps = [
    {
      id: OrderStatus.REVIEWING,
      order: 0,
      title: 'Revisando',
      tagline: '¿Está todo bien con el pedido?',
      icon: <ChefHat />,
      btnLabel: 'Preparar',
      btnAction: () =>
        updateOrderStatus({
          organizationId,
          orderId: order.id,
          status: OrderStatus.PREPARING,
        }),
    },
    {
      id: OrderStatus.PREPARING,
      order: 1,
      title: 'Preparando',
      tagline: '¿Está listo el pedido?',
      icon: <Truck />,
      btnLabel: 'Enviar',
      btnAction: () =>
        updateOrderStatus({
          organizationId,
          orderId: order.id,
          status: OrderStatus.SENDING,
        }),
    },
    {
      id: OrderStatus.SENDING,
      order: 2,
      title: 'Enviando',
      tagline: '¿Completaste el pedido?',
      icon: <PackageCheck />,
      btnLabel: 'Entregar',
      btnAction: () =>
        updateOrderStatus({
          organizationId,
          orderId: order.id,
          status: OrderStatus.DELIVERED,
        }),
    },
    {
      id: OrderStatus.DELIVERED,
      order: 3,
      title: 'Completado',
      tagline: 'Pedido Completado',
      icon: <CheckCheck />,
      btnLabel: 'Completar',
      btnAction: async () =>
        await completeOrder({
          order,
          organizationId,
          orderId: order.id,
        }),
      handleClose,
    },
  ];

  const authClient = useMemo(
    () => getUserById(order?.client as string) as User,
    [getUserById, order?.client]
  );

  const anonClient = order.client;

  const client: AnonymousUser | User = authClient ?? anonClient;

  const activeStep = useMemo(() => {
    return steps.findIndex((s) => s.id === order.status) ?? 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.status]);

  const handleNext = async () => {
    try {
      await steps[activeStep].btnAction?.();
      openSnackbar('Tu orden ha sido movida a la siguiente etapa', 'success'); //TODO later: if is last step show a different message
    } catch (error) {
      console.error(error);
      openSnackbar('Ocurrió un error al mover tu orden', 'error');
      throw error;
    }
  };

  const subtotal = useMemo(() => {
    if (!order?.items?.length) return 0;

    return order.items
      .map((i) => multiplyByQuantity(i.price, i.quantity))
      .reduce((acc, curr) => acc + curr, 0);
  }, [order?.items]);

  const taxes = useMemo(() => {
    return getPercentage(subtotal, 15);
  }, [subtotal]);

  return (
    <Stack spacing={2} flex={1}>
      <Paper>
        <Stack spacing={3} p={2}>
          <Stack
            sx={{
              width: '100%',
              backgroundColor: '#DDEAFD',
              borderRadius: 1,
              px: 2,
              py: 1.5,
            }}
          >
            <Stepper
              activeStep={activeStep}
              // orientation={isMobile ? 'vertical' : 'horizontal'} // TODO: Try later
              alternativeLabel
            >
              {steps.map((step) => {
                const stepProps: { completed?: boolean } = {};
                const labelProps: {
                  optional?: React.ReactNode;
                } = {};

                return (
                  <Step
                    key={step.id}
                    {...stepProps}
                    sx={{
                      '& .MuiStepLabel-iconContainer .Mui-completed': {
                        color: palette.success.main,
                      },
                    }}
                  >
                    <StepLabel {...labelProps}>{step.title}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Stack>
          <Stack spacing={2} py={2} alignItems="center">
            <Typography variant="h3">{steps[activeStep]?.tagline}</Typography>
            {steps[activeStep]?.btnAction ? (
              <LoadingButton
                size="large"
                color="warning"
                variant="contained"
                startIcon={steps[activeStep].icon}
                onClick={handleNext}
                loading={isUpdatingStatusMutation.isLoading}
              >
                {steps[activeStep].btnLabel}
              </LoadingButton>
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
        {order?.createdAt ? (
          <DataCard title="Tiempos">
            <Stack spacing={1}>
              <DataCell
                label="Fecha/Hora"
                value={format(order?.createdAt, 'p')}
              />

              <DataCell
                label="Entrega"
                value={format(addMinutes(order?.createdAt, 45), 'p')}
              />
            </Stack>
          </DataCard>
        ) : null}
        <DataCard title="Cliente">
          <Stack spacing={1}>
            {client?.name ? (
              <DataCell
                label="Nombre"
                value={nameWithFirstLastnameInitial(
                  client?.name,
                  client?.lastname
                )}
              />
            ) : null}
            {client?.phone ? (
              <DataCell label="Teléfono" value={client.phone} />
            ) : null}
          </Stack>
        </DataCard>
      </Stack>

      <DataCard title="Platos">
        <>
          <Stack spacing={1}>
            {order?.items?.map((i) => (
              <Stack key={`order-item-${i.id}`} direction={'row'} spacing={2}>
                <Typography variant="body2">{i.quantity}</Typography>
                <Typography variant="body2" flex={1}>
                  {i.title}
                </Typography>
                <Typography variant="body2">
                  {addCurrency(multiplyByQuantity(i.price, i.quantity), 'NIO')}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Divider />
          <Stack spacing={1}>
            {subtotal ? (
              <DataCell label="Subtotal" value={addCurrency(subtotal, 'NIO')} />
            ) : null}
            {taxes && currentOrganization.settings.includeTax ? (
              <DataCell label="IVA" value={addCurrency(taxes, 'NIO')} />
            ) : null}
            <DataCell
              label="Total"
              value={addCurrency(
                sumAmounts([
                  subtotal,
                  currentOrganization.settings.includeTax ? taxes : 0,
                ]),
                'NIO'
              )}
            />
          </Stack>
        </>
      </DataCard>
    </Stack>
  );
};

export default OrderDetailsTab;
